import { useEffect } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { makeStyles } from '@material-ui/styles';
import { useSetAtom } from 'jotai';
import cn from 'clsx';

import { MainButton } from '@bizzabo/design-system';
import { instanaReportError } from '@bizzabo/instana-api';

import useSchedulingPreferencesForm, {
    formatAvailability,
    withDefaults,
} from './useSchedulingPreferencesForm';
import {
    Availability,
    SchedulingPreferencesFormValues,
} from './useSchedulingPreferencesForm/types';
import useUpdateSchedulingPreferences from './hooks/useUpdateSchedulingPreferences';
import useSchedulingPreferences from '../../hooks/useSchedulingPreferences';
import useTypeTracking from '../../../analytics';
import SchedulingPreferencesFormFields from './SchedulingPreferencesFormFields';
import { useUserSettings } from '../../../settingsNav';
import SaveButton from '../../components/SaveButton';
import MeetingSettingsModalThemeProvider from '../../../customizationTheme/MeetingSettingsModalThemeProvider';
import currentTimeZoneIdAtom, {
    setCurrentTimeZoneId as setTimeZoneId,
} from '../../../meetings/schedule/timeZoneId';
import { validateEmptyInterval } from './helpers';

export {
    SchedulingPreferencesFormFields,
    useSchedulingPreferencesForm,
    formatAvailability,
    withDefaults,
    useUpdateSchedulingPreferences,
};
export type { SchedulingPreferencesFormValues };
interface SchedulingPreferencesFormProps {
    eventId: number;
}

export const SchedulingPreferencesForm: React.FC<SchedulingPreferencesFormProps> = ({
    eventId,
}) => {
    const modal = useUserSettings();
    const { trackClickEvent } = useTypeTracking({ data: { category: 'MEETINGS_SETTINGS' } });
    const updateSchedulingPreferences = useUpdateSchedulingPreferences();

    const {
        isLoading: isLoadingData,
        data: schedulingPreferencesData,
        isError,
    } = useSchedulingPreferences(eventId);

    const form = useSchedulingPreferencesForm(withDefaults({}));
    const { handleSubmit, formState, reset, clearErrors, setError } = form;
    const formHasErrors = Object.keys(formState.errors).length > 0;

    useEffect(() => {
        reset(withDefaults(schedulingPreferencesData));
    }, [schedulingPreferencesData, reset]);

    const classes = useStyles();

    const setCurrentTimeZoneId = useSetAtom(currentTimeZoneIdAtom);

    if (isLoadingData || isError) {
        return null;
    }

    const formEmptyValidation = (availability: Availability['availability']) => {
        clearErrors('availability');
        const errors = validateEmptyInterval(availability);
        errors.forEach(({ field, message }) => {
            setError(field, { message });
        });
        return errors;
    };

    const onSubmit: SubmitHandler<SchedulingPreferencesFormValues> = formValues => {
        const errors = formEmptyValidation(formValues.availability);
        if (errors.length) {
            return;
        }

        setCurrentTimeZoneId(formValues.timezoneId);
        setTimeZoneId(formValues.timezoneId);
        trackClickEvent({ subCategory: 'SAVE_MANAGEMENT_SETTINGS' });
        try {
            updateSchedulingPreferences({
                eventId: eventId,
                payload: {
                    timezoneId: formValues.timezoneId,
                    meetingDuration: formValues.meetingDuration,
                    schedulingHours: formatAvailability(
                        formValues.availability,
                        formValues.timezoneId
                    ),
                    bufferTime: formValues.bufferTime,
                    leadTime: formValues.leadTime,
                    maxMeetingsPerDay: formValues.maxMeetingsPerDay,
                },
                schedulingPreferencesData,
            });
        } catch (error) {
            instanaReportError({ error });
        }
    };

    const handleCancelClick = () => {
        trackClickEvent({ subCategory: 'CANCEL_MANAGEMENT_SETTINGS' });
        modal.closeUserSettings();
    };

    return (
        <MeetingSettingsModalThemeProvider>
            <div className={classes.schedulingPreferencesContainer}>
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    className={classes.schedulingPreferencesForm}
                >
                    <div className={classes.formFields}>
                        <SchedulingPreferencesFormFields
                            form={form}
                            schedulingHoursBase={schedulingPreferencesData.schedulingHoursBase}
                        />
                    </div>
                    <div className={classes.saveFooter}>
                        <MainButton
                            className={cn(classes.cancelButton, classes.formButton)}
                            onClick={handleCancelClick}
                            disabled={formState.isSubmitting}
                        >
                            Cancel
                        </MainButton>
                        <SaveButton
                            className={classes.formButton}
                            disabled={!formState.isDirty || formHasErrors}
                            isLoading={formState.isSubmitting}
                            isSubmitSuccesful={formState.isSubmitSuccessful}
                            type="submit"
                            variant="fill"
                        />
                    </div>
                </form>
            </div>
        </MeetingSettingsModalThemeProvider>
    );
};

const useStyles = makeStyles(
    () => ({
        schedulingPreferencesContainer: {
            padding: '20px 30px 16px 30px',
            height: '100%',
        },
        schedulingPreferencesForm: {
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
        },
        formFields: {
            paddingLeft: 2,
            position: 'relative',
            overflow: 'auto',
            paddingBottom: 100,
        },
        saveFooter: {
            borderBottomRightRadius: 33,
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'center',
            paddingRight: 32,
            paddingTop: 16,
        },
        formButton: {
            height: 40,
        },
        cancelButton: {
            border: 0,
            marginRight: 16,
            '&:disabled': {
                border: 0,
            },
        },
    }),
    { classNamePrefix: 'people-settings-scheduling-preferences' }
);

export default SchedulingPreferencesForm;
